import { useState } from 'react';
import { useArtifactStore } from '../hooks/useArtifactStore';
import type { WidgetProps } from './WidgetRegistry';

export function QueryResultsWidget(_props: WidgetProps) {
  const { testResult, testing, error, clearTest } = useArtifactStore();
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  if (testing) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-tower-muted text-sm animate-pulse">Executing query...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <div className="text-error text-xs font-semibold mb-1">Error</div>
        <div className="text-error text-xs font-mono whitespace-pre-wrap">{error}</div>
      </div>
    );
  }

  if (!testResult) {
    return (
      <div className="flex items-center justify-center h-full text-tower-muted text-sm">
        Run a test to see results
      </div>
    );
  }

  const rows = (testResult.rows || testResult.sampleRows || []) as Record<string, unknown>[];
  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

  const sorted = sortCol
    ? [...rows].sort((a, b) => {
        const va = a[sortCol] ?? '';
        const vb = b[sortCol] ?? '';
        const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
        return sortAsc ? cmp : -cmp;
      })
    : rows;

  function handleSort(col: string) {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(true); }
  }

  function handleExport() {
    if (columns.length === 0) return;
    const header = columns.join(',');
    const body = rows.map(r => columns.map(c => JSON.stringify(r[c] ?? '')).join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'query-results.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex flex-col h-full">
      {/* Summary bar */}
      <div className="flex items-center gap-3 px-2 py-1 border-b border-tower-border/50 shrink-0 text-xs">
        <span className={testResult.success ? 'text-success' : 'text-error'}>
          {testResult.success ? 'OK' : 'FAILED'}
        </span>
        <span className="text-tower-muted">{testResult.rowCount} rows</span>
        <span className="text-tower-muted">{testResult.executionMs}ms</span>
        {testResult.error && <span className="text-error truncate">{testResult.error}</span>}
        <div className="flex-1" />
        <button onClick={handleExport} className="text-tower-muted hover:text-tower-accent">CSV</button>
        <button onClick={clearTest} className="text-tower-muted hover:text-tower-danger">Clear</button>
      </div>

      {/* Data table */}
      <div className="flex-1 overflow-auto">
        {columns.length === 0 ? (
          <div className="p-3 text-tower-muted text-xs">{testResult.error || 'No rows returned'}</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-tower-surface">
              <tr>
                {columns.map((col) => (
                  <th key={col}
                    onClick={() => handleSort(col)}
                    className="text-left px-2 py-1 text-tower-muted font-semibold border-b border-tower-border cursor-pointer hover:text-tower-accent whitespace-nowrap">
                    {col} {sortCol === col && (sortAsc ? '↑' : '↓')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i} className="hover:bg-tower-border/20">
                  {columns.map((col) => (
                    <td key={col} className="px-2 py-1 border-b border-tower-border/30 text-tower-text whitespace-nowrap max-w-[300px] truncate">
                      {row[col] === null ? <span className="text-tower-muted italic">null</span> : String(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
